import { z } from 'zod';
import { errResult } from 'libs/end-to-end/src/result';
import { okResult } from 'libs/end-to-end/src/result';
import { Result } from 'libs/end-to-end/src/result';
import { CompileDataRulePatternError } from 'libs/end-to-end/src/types/errors/compile-data-rule-pattern-error';
import { guardExhaustive } from 'libs/end-to-end/src/generic';

export const DataRulePatternSegmentText = z.object({
  type: z.literal('text'),
  text: z.array(
    z.object({
      text: z.string(),
      isCaseSensitive: z.boolean(),
    }),
  ),
});

export type DataRulePatternSegmentText = z.infer<
  typeof DataRulePatternSegmentText
>;

export const DataRulePatternSegmentWildcardStringExcludingModes = [
  'nothing',
  'characters',
  'text',
] as const;

export const DataRulePatternSegmentWildcardStringExcludingMode = z.enum(
  DataRulePatternSegmentWildcardStringExcludingModes,
);

export type DataRulePatternSegmentWildcardStringExcludingMode = z.infer<
  typeof DataRulePatternSegmentWildcardStringExcludingMode
>;

export const DataRulePatternSegmentWildcardString = z.object({
  type: z.literal('wildcard-string'),
  excludingMode: DataRulePatternSegmentWildcardStringExcludingMode,
  excludingCharacters: z.string(),
  excludingCharactersIsCaseSensitive: z.boolean(),
  excludingText: z.array(
    z.object({
      text: z.string(),
      isCaseSensitive: z.boolean(),
    }),
  ),
});

export type DataRulePatternSegmentWildcardString = z.infer<
  typeof DataRulePatternSegmentWildcardString
>;

export const DataRulePatternSegmentWildcardNumber = z.object({
  type: z.literal('wildcard-number'),
  minimum: z.number().optional(),
  maximum: z.number().optional(),
  allowNanAndInfinity: z.boolean(),
});

export type DataRulePatternSegmentWildcardNumber = z.infer<
  typeof DataRulePatternSegmentWildcardNumber
>;

export const DataRulePatternSegmentWildcardDate = z.object({
  type: z.literal('wildcard-date'),
  // These are both in the precise format of YYYY-MM-DD (if you want a different format in the future, add a versioned type).
  minimum: z.string().optional(),
  maximum: z.string().optional(),
});

export type DataRulePatternSegmentWildcardDate = z.infer<
  typeof DataRulePatternSegmentWildcardDate
>;

export const DataRulePatternSegmentWhitespace = z.object({
  type: z.literal('whitespace'),
});

export type DataRulePatternSegmentWhitespace = z.infer<
  typeof DataRulePatternSegmentWhitespace
>;

export const DataRulePatternSegment = z.union([
  DataRulePatternSegmentText,
  DataRulePatternSegmentWildcardString,
  DataRulePatternSegmentWildcardNumber,
  DataRulePatternSegmentWildcardDate,
  DataRulePatternSegmentWhitespace,
]);

export type DataRulePatternSegment = z.infer<typeof DataRulePatternSegment>;

export type DataRulePatternSegmentType = DataRulePatternSegment['type'];
export const DataRulePatternSegmentTypes = [
  'text',
  'wildcard-string',
  'wildcard-number',
  'wildcard-date',
  'whitespace',
] as const;

export const DataRulePattern = z.array(DataRulePatternSegment);

export type DataRulePattern = z.infer<typeof DataRulePattern>;

export const DataRule = z.object({
  headerPattern: DataRulePattern,
  valuePattern: DataRulePattern,
});

export type DataRule = z.infer<typeof DataRule>;

export type DataRulePatternValidationFn = (value: string) => boolean;

export interface DataRulePatternCompilation {
  pattern: DataRulePattern;
  validationFn: DataRulePatternValidationFn;
  sampleValues: string[];
}

/** djb2 hash → 32‑bit unsigned integer */
const hashString = (str: string): number => {
  let h = 5381 >>> 0;
  for (let i = 0; i < str.length; i++) {
    h = ((h << 5) + h + str.charCodeAt(i)) >>> 0;
  }
  return h;
};

/** Very small LCG for deterministic pseudo‑randomness */
const makeRng = (seed: number) => {
  return () => {
    seed = (seed * 1664525 + 1013904223) >>> 0;
    return seed / 0xffffffff;
  };
};

const alphabet =
  'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
const whitespaceVariants = [' ', '\t  \n'];

/** Random string that avoids any character in `forbidden` */
const randomString = (
  length: number,
  forbidden: Set<string>,
  rnd: () => number,
) => {
  const allowed = alphabet.split('').filter((c) => !forbidden.has(c));
  if (allowed.length === 0) return '_'.repeat(length); // fallback
  let out = '';
  for (let i = 0; i < length; i++) {
    out += allowed[Math.floor(rnd() * allowed.length)];
  }
  return out;
};

const toMixedCase = (s: string, rnd: () => number): string =>
  Array.from(s)
    .map((c) => (rnd() < 0.5 ? c.toLowerCase() : c.toUpperCase()))
    .join('');

const addDays = (iso: string, days: number): string => {
  const d = new Date(iso + 'T00:00:00Z');
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
};

/**
 * Turn an arbitrary literal string into a RegExp‑safe pattern.
 * When `isCaseSensitive === false`, every character is enlarged to a
 * character‑class that contains *all* of its single‑code‑point case
 * variants.  This reproduces exactly what JS does internally for `/i`
 * (Unicode *simple* case‑folding: no multi‑char expansions such as “ß”→“SS”).
 *
 * Example:
 *   encodeLiteralRegexPattern({ text: "áßA.", isCaseSensitive: false })
 *   // → "[áÁ]ß[Aa]\\.  (ready to inject in a bigger pattern)
 */
export function encodeLiteralRegexPattern(params: {
  text: string;
  isCaseSensitive: boolean;
  // If you pass true here, it is assumed you will use the return value inside of a exclusion character match (aka [^...]).
  asSubPattern?: boolean;
}): string {
  const { text, isCaseSensitive, asSubPattern } = params;

  /** Escape outside a […] class */
  const esc = (c: string) => (/[\]\\.^$*+?()[\]{}|]/.test(c) ? `\\${c}` : c);
  /** Escape *inside* a […] class  */
  const escClass = (c: string) => (/[\]\\\-\]^]/.test(c) ? `\\${c}` : c);

  // fast path ─ no case juggling needed
  if (isCaseSensitive) return [...text].map(esc).join('');

  // case‑insensitive path
  return [...text]
    .map((ch) => {
      const up = ch.toUpperCase();
      const lo = ch.toLowerCase();

      // Collect every *other* single‑code‑point casing
      const alts = new Set<string>([ch]);
      if (up !== ch && up.length === 1) alts.add(up);
      if (lo !== ch && lo.length === 1) alts.add(lo);

      // If there is at least one alternative, build a class; else a literal
      return alts.size > 1
        ? `${asSubPattern ? '' : '['}${[...alts].map(escClass).join('')}${asSubPattern ? '' : ']'}`
        : esc(ch);
    })
    .join('');
}

const REGEX_ANY_STRING = '.+';
const REGEX_ANY_NUMBER =
  '([\\-\\+]?[iI][nN][fF])|([nN][aA][nN])|([+\\-]?(\\d+\\.\\d*|\\d*\\.\\d+|\\d+)([eE][+\\-]?\\d+)?)';
const REGEX_ANY_DATE =
  '[1-9][0-9][0-9][0-9]\\-(0[1-9]|11|12)\\-(0[1-9]|[12][0-9]|3[0-1])';
const REGEX_ANY_WHITESPACE = '[\\s\\t\\n\\r]+';

export const compileDataRulePattern = (
  pattern: DataRulePattern,
): Result<DataRulePatternCompilation, CompileDataRulePatternError> => {
  // Validation
  if (pattern.length === 0) {
    return errResult({
      errorType: 'CompileDataRulePatternSequenceEmptyError',
    });
  }

  let lastWasWildcard = false;

  for (let segmentIndex = 0; segmentIndex < pattern.length; segmentIndex++) {
    const segment = pattern[segmentIndex];

    if (!segment) {
      continue;
    }

    const thisIsWildcard =
      segment.type === 'wildcard-string' ||
      segment.type === 'wildcard-number' ||
      segment.type === 'wildcard-date' ||
      segment.type === 'whitespace';

    if (thisIsWildcard && lastWasWildcard) {
      return errResult({
        errorType: 'CompileDataRulePatternSequenceOfWildcardsError',
        invalidSegmentIndexes: [segmentIndex - 1, segmentIndex],
      });
    }

    // Let's check if this has some bad boundaries.
    if (
      segment.type === 'wildcard-number' &&
      segment.minimum !== undefined &&
      segment.maximum !== undefined &&
      segment.minimum > segment.maximum
    ) {
      return errResult({
        errorType: 'CompileDataRulePatternSequenceBadBoundsError',
        invalidSegmentIndex: segmentIndex,
      });
    }

    if (
      segment.type === 'wildcard-date' &&
      segment.minimum !== undefined &&
      segment.maximum !== undefined &&
      segment.minimum > segment.maximum
    ) {
      return errResult({
        errorType: 'CompileDataRulePatternSequenceBadBoundsError',
        invalidSegmentIndex: segmentIndex,
      });
    }

    lastWasWildcard = thisIsWildcard;
  }

  const validationFn: DataRulePatternValidationFn = (value: string) => {
    if (pattern.length === 0) {
      return false;
    }

    // regex always starts with ^
    let finalPattern = '^';

    // loop through segments and add a group for each segment

    for (let segmentIndex = 0; segmentIndex < pattern.length; segmentIndex++) {
      const segment = pattern[segmentIndex];

      if (!segment) {
        continue;
      }

      const segmentGroupName = `?<i${segmentIndex}>`;

      finalPattern += '(';
      finalPattern += segmentGroupName;

      if (segment.type === 'text') {
        let segmentRegex = '';
        let isFirstInSegment = true;

        for (const { text, isCaseSensitive } of segment.text) {
          if (!isFirstInSegment) {
            segmentRegex += '|';
          }
          segmentRegex += `(${encodeLiteralRegexPattern({
            text,
            isCaseSensitive,
          })})`;
          isFirstInSegment = false;
        }

        finalPattern += segmentRegex;
      } else if (segment.type === 'wildcard-string') {
        if (segment.excludingMode === 'characters') {
          // We use the character exclusion method when possible, but can not for string based exclusions because regex look-ahead is limited.
          finalPattern += `[^${encodeLiteralRegexPattern({
            text: segment.excludingCharacters,
            isCaseSensitive: segment.excludingCharactersIsCaseSensitive,
            asSubPattern: true,
          })}]+`;
        } else {
          finalPattern += REGEX_ANY_STRING;
        }
      } else if (segment.type === 'wildcard-number') {
        finalPattern += REGEX_ANY_NUMBER;
      } else if (segment.type === 'wildcard-date') {
        finalPattern += REGEX_ANY_DATE;
      } else if (segment.type === 'whitespace') {
        finalPattern += REGEX_ANY_WHITESPACE;
      } else {
        throw guardExhaustive(segment);
      }

      finalPattern += ')';
    }

    // regex always ends with $
    finalPattern += '$';

    // compile the regex
    const regex = new RegExp(finalPattern, 's');

    // test our string and extract each group named
    const match = value.match(regex);
    if (!match) {
      return false;
    }

    for (let segmentIndex = 0; segmentIndex < pattern.length; segmentIndex++) {
      const segment = pattern[segmentIndex];

      if (!segment) {
        continue;
      }

      const groupName = `i${segmentIndex}`;
      const groupValue = match.groups?.[groupName];

      if (groupValue === undefined) {
        return false;
      }

      // Now we need to enforce other segment rules, like range checks.
      if (
        segment.type === 'wildcard-string' &&
        segment.excludingMode === 'text'
      ) {
        // We need to check if the groupValue contains any of the excluded text.
        for (const { text, isCaseSensitive } of segment.excludingText) {
          const regex = new RegExp(
            `^${encodeLiteralRegexPattern({ text, isCaseSensitive })}$`,
            's',
          );
          if (regex.test(groupValue)) {
            return false;
          }
        }
      }

      if (segment.type === 'wildcard-number') {
        const isNanOrInfinity =
          groupValue.toLowerCase() === 'nan' ||
          groupValue.toLowerCase() === 'inf' ||
          groupValue.toLowerCase() === '+inf' ||
          groupValue.toLowerCase() === '-inf';

        if (isNanOrInfinity) {
          if (!segment.allowNanAndInfinity) {
            return false;
          }
        } else {
          let numberValue = 0;

          try {
            numberValue = parseFloat(groupValue);
          } catch (e) {
            console.log(`Error parsing number: ${e}`);
            return false;
          }

          if (
            (segment.minimum !== undefined && numberValue < segment.minimum) ||
            (segment.maximum !== undefined && numberValue > segment.maximum)
          ) {
            return false;
          }
        }
      }

      if (segment.type === 'wildcard-date') {
        const groupValueAsDate = new Date(groupValue);
        const segmentMinimumAsDate =
          segment.minimum === undefined ? undefined : new Date(segment.minimum);
        const segmentMaximumAsDate =
          segment.maximum === undefined ? undefined : new Date(segment.maximum);

        if (
          (segmentMinimumAsDate !== undefined &&
            groupValueAsDate < segmentMinimumAsDate) ||
          (segmentMaximumAsDate !== undefined &&
            groupValueAsDate > segmentMaximumAsDate)
        ) {
          return false;
        }
      }
    }

    return true;
  };

  // ───────────── NEW: build segment‑level variant pools ─────────────
  const seed = hashString(JSON.stringify(pattern));
  const rnd = makeRng(seed);

  const segmentVariants: string[][] = pattern.map((segment) => {
    switch (segment.type) {
      // ─── text ───────────────────────────────────────
      case 'text': {
        const variants: string[] = [];
        segment.text.forEach(({ text, isCaseSensitive }) => {
          variants.push(text); // original
          if (!isCaseSensitive) {
            variants.push(toMixedCase(text, rnd)); // mixed‑case demo
          }
        });
        return variants.length ? variants : [''];
      }

      // ─── wildcard‑string ────────────────────────────
      case 'wildcard-string': {
        const baseLen = 8 + Math.floor(rnd() * 8);

        if (segment.excludingMode === 'nothing') {
          /* … unchanged … */
        }

        if (segment.excludingMode === 'characters') {
          /* … unchanged … */
        }

        //  excludingMode === 'text'
        const variants: string[] = [];

        // ➊ Cheeky “_not {text}_” demo for every forbidden substring
        segment.excludingText.forEach(({ text }) => {
          variants.push(`_not ${text}_`);
        });

        // ➋ Keep one regular long string to show normal variance
        variants.push(
          randomString(baseLen + 4, new Set(), rnd) /* long random fallback */,
        );

        return variants;
      }

      // ─── wildcard‑number ────────────────────────────
      case 'wildcard-number': {
        const v: string[] = [];
        if (segment.minimum !== undefined) v.push(segment.minimum.toString());
        if (segment.maximum !== undefined) v.push(segment.maximum.toString());

        if (
          segment.minimum !== undefined &&
          segment.maximum !== undefined &&
          segment.minimum !== segment.maximum
        ) {
          v.push(((segment.minimum + segment.maximum) / 2).toString());
        } else if (v.length === 0) {
          // no bounds – one neg, one pos
          v.push('-42.1', '9876.543');
        }

        if (segment.allowNanAndInfinity) {
          const tokens = ['nan', '+inf', '-inf', 'inf'];
          v.push(tokens[Math.floor(rnd() * tokens.length)]);
        }
        return v;
      }

      // ─── wildcard‑date ──────────────────────────────
      case 'wildcard-date': {
        const dates: string[] = [];
        const today = new Date().toISOString().slice(0, 10); // YYYY‑MM‑DD

        if (segment.minimum) {
          dates.push(addDays(segment.minimum, 1), segment.minimum);
        }
        if (segment.maximum) {
          dates.push(segment.maximum, addDays(segment.maximum, -1));
        }
        if (
          segment.minimum &&
          segment.maximum &&
          segment.minimum !== segment.maximum
        ) {
          // mid‑point
          const minT = new Date(segment.minimum + 'T00:00:00Z').getTime();
          const maxT = new Date(segment.maximum + 'T00:00:00Z').getTime();
          const mid = new Date((minT + maxT) / 2).toISOString().slice(0, 10);
          dates.push(mid);
        }
        if (dates.length === 0) dates.push(today, addDays(today, -1));
        return dates;
      }

      // ─── whitespace ─────────────────────────────────
      case 'whitespace':
        return whitespaceVariants;

      default:
        return [''];
    }
  });

  // ───────────── Combine pools into full pattern samples ─────────────
  const maxLen = Math.max(...segmentVariants.map((v) => v.length));
  const sampleValues: string[] = [];

  for (let i = 0; i < maxLen; i++) {
    const sample = segmentVariants
      .map((variants) => variants[i % variants.length])
      .join('');
    sampleValues.push(sample);
  }
  // Safety cap (very unlikely to hit unless someone feeds absurd patterns)
  if (sampleValues.length > 30) sampleValues.length = 30;

  return okResult({
    pattern,
    validationFn,
    sampleValues,
  });
};

export const DATA_RULES_PRESET_ALL: DataRule[] = [
  {
    headerPattern: [
      {
        type: 'wildcard-string',
        excludingMode: 'nothing',
        excludingCharacters: '',
        excludingCharactersIsCaseSensitive: false,
        excludingText: [],
      },
    ],
    valuePattern: [
      {
        type: 'wildcard-string',
        excludingMode: 'nothing',
        excludingCharacters: '',
        excludingCharactersIsCaseSensitive: false,
        excludingText: [],
      },
    ],
  },
];
